import React, { FC } from 'react';
import { Row, Col } from 'antd';
import InputField from 'shared/components/InputField';
import styles from '../../activityDataEntry.module.scss';
import { Scope1DesiredData } from 'models/ActivityDataScope1DesiredData/scope1DesiredData.model';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import COSelect from 'shared/components/COSelect';
import { ReactComponent as Close } from 'assets/icons/close_enclosed.svg';
import { BestFitRegion } from 'models/BestFitRegion/bestFitRegion.model';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import { allowOnlyNumbers } from 'shared/utils/convertFormData';
import { CategoryOptions } from 'shared/utils/convertOptions';

interface FugitiveEmissionsFieldsProps {
  handleToolTipOpen: (fieldName: HelpDeskInputFieldEnum) => () => void;
  isEditView: boolean;
  refrigerantOptions?: CategoryOptions[];
  fetchIngredientOrigin: (params?: {
    activity_label?: string;
  }) => Promise<BestFitRegion[] | undefined>;
  handleMonthlyModalOpen: (
    source?: ScopeData,
    key?: string,
    selectedIndex?: string,
    index?: number
  ) => () => void;
  handleSwitch: (
    source?: ScopeData,
    key?: string,
    selectedIndex?: string,
    selectedItemIndex?: number
  ) => () => void;
}

const FugitiveEmissionsFields: FC<FugitiveEmissionsFieldsProps> = ({
  handleToolTipOpen,
  isEditView,
  refrigerantOptions,
  fetchIngredientOrigin,
  handleMonthlyModalOpen,
  handleSwitch,
}) => {
  const { values, setFieldValue } = useFormikContext<Scope1DesiredData>();
  const defaultScopeItem = {
    id: undefined,
    fuelValue: null,
    fuelName: 'refrigerant',
    refrigerantRegionSpecId: null,
    refrigerantLabel: null,
    scopeFieldCategory: 'Fugitive Emissions',
    scopeCategory: 'scope1',
    unit: null,
    isYearly: true,
  };
  const handleRefrigerantChange =
    (fieldName: string) => async (activity_label: string) => {
      let fieldValue = undefined;
      if (activity_label) {
        const data = await fetchIngredientOrigin({
          activity_label: activity_label,
        });
        fieldValue = data?.[0]?.id ?? '';
      }
      setFieldValue(fieldName, Number(fieldValue));
    };
  const handleRemove =
    (index: number, arrayHelpers: FieldArrayRenderProps) => () =>
      arrayHelpers.remove(index);
  const handlePush = (arrayHelpers: FieldArrayRenderProps) => () =>
    arrayHelpers.push(defaultScopeItem);

  return (
    <FieldArray
      name="fugitiveEmissions"
      render={arrayHelpers => (
        <>
          {values?.fugitiveEmissions?.map((source, index) => (
            <Row
              gutter={[24, 40]}
              className={styles.fugitiveEmissionsFormConatiner}
              key={source?.fuelName}>
              <Col span={12}>
                <div className={styles.fieldContainer}>
                  <Col span={16}>
                    <InputField
                      name={`fugitiveEmissions.${index}.fuelValue`}
                      placeholderstring="Refrigerant"
                      label="Refrigerant (kg)"
                      info
                      infoClick={handleToolTipOpen(
                        HelpDeskInputFieldEnum.refrigerant
                      )}
                      disabled={
                        !isEditView ||
                        !values?.fugitiveEmissions?.[index].isYearly
                      }
                      onKeyDown={allowOnlyNumbers}
                      isNullable={true}
                    />
                  </Col>
                  <Col span={16}>
                    <COSelect
                      name={`fugitiveEmissions.${index}.refrigerantLabel`}
                      placeholderstring="Type"
                      label="Type"
                      options={refrigerantOptions}
                      onChange={handleRefrigerantChange(
                        `fugitiveEmissions[${index}].refrigerantRegionSpecId`
                      )}
                      disabled={!isEditView}
                    />
                  </Col>
                  {!values?.fugitiveEmissions?.[index].isYearly && (
                    <>
                      <Col span={6}>
                        <p
                          className={styles.viewMonthlyText}
                          onClick={handleMonthlyModalOpen(
                            values?.fugitiveEmissions?.[index],
                            'fugitiveEmissions',
                            `fugitiveEmissions.${index}`,
                            index
                          )}>
                          View Monthly data
                        </p>
                      </Col>
                      {isEditView && (
                        <span className={styles.pipeSeperator}> | </span>
                      )}
                    </>
                  )}
                  {isEditView && (
                    <Col span={8}>
                      <p
                        className={styles.monthlyText}
                        onClick={handleSwitch(
                          values?.fugitiveEmissions?.[index],
                          'fugitiveEmissions',
                          `fugitiveEmissions.${index}`,
                          index
                        )}>
                        {values?.fugitiveEmissions?.[index].isYearly
                          ? 'Switch to monthly'
                          : 'Switch to annually'}
                      </p>
                    </Col>
                  )}
                  {values.fugitiveEmissions?.length &&
                    isEditView &&
                    values.fugitiveEmissions.length > 1 && (
                      <Col span={1} onClick={handleRemove(index, arrayHelpers)}>
                        <Close />
                      </Col>
                    )}
                </div>
              </Col>
            </Row>
          ))}
          {values.fugitiveEmissions?.length &&
            isEditView &&
            values.fugitiveEmissions?.length < 5 && (
              <div
                className={styles.addRefrigirant}
                onClick={handlePush(arrayHelpers)}>
                <span>+</span> Add Refrigerant
              </div>
            )}
        </>
      )}
    />
  );
};

export default FugitiveEmissionsFields;
